import React from 'react';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Shield, Database, MapPin, Activity, Bell, Trash2, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pb-20">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:text-[#6E2BFF]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#6E2BFF]" />
            <div>
              <h1 className="text-2xl font-bold">Politique de confidentialité</h1>
              <p className="text-sm text-[#9CA0AB]">Dernière mise à jour : février 2026</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <Card className="p-6 mb-6">
          <p className="text-[#B3B6C2] leading-relaxed">
            ZNO est une application de jeux sportifs en conditions réelles (playgrounds).
            La protection de vos données est une priorité.
          </p>
        </Card>

        {/* Données collectées */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-6 h-6 text-[#6E2BFF]" />
            <h2 className="text-xl font-bold">Données collectées</h2>
          </div>
          <p className="text-[#9CA0AB] mb-4">
            Nous collectons uniquement les données nécessaires au fonctionnement de l'application :
          </p>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-white mb-2">Compte utilisateur</h3>
              <ul className="list-disc list-inside text-[#B3B6C2] space-y-1 ml-2">
                <li>pseudo</li>
                <li>identifiant interne</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#6E2BFF]" />
                Données de localisation
              </h3>
              <p className="text-[#B3B6C2] mb-2">utilisées uniquement pour :</p>
              <ul className="list-disc list-inside text-[#B3B6C2] space-y-1 ml-2">
                <li>afficher les terrains à proximité</li>
                <li>vérifier la présence physique sur un terrain (check-in)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#6E2BFF]" />
                Données d'activité
              </h3>
              <ul className="list-disc list-inside text-[#B3B6C2] space-y-1 ml-2">
                <li>duels joués</li>
                <li>scores</li>
                <li>terrains possédés</li>
                <li>événements liés au jeu</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2">Données techniques</h3>
              <ul className="list-disc list-inside text-[#B3B6C2] space-y-1 ml-2">
                <li>type d'appareil</li>
                <li>logs d'erreur anonymisés</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Utilisation des données */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">🎯 Utilisation des données</h2>
          <p className="text-[#9CA0AB] mb-3">Les données sont utilisées exclusivement pour :</p>
          <ul className="list-disc list-inside text-[#B3B6C2] space-y-2 mb-4 ml-2">
            <li>permettre le fonctionnement du jeu</li>
            <li>gérer les duels et scores</li>
            <li>afficher les terrains et événements locaux</li>
            <li>prévenir la fraude (multi-comptes, faux check-ins)</li>
            <li>envoyer des notifications liées au jeu uniquement</li>
          </ul>
          <div className="bg-[#6E2BFF]/10 border border-[#6E2BFF]/30 rounded-lg p-4">
            <p className="text-white font-semibold">
              👉 Aucune donnée n'est utilisée à des fins publicitaires.
            </p>
          </div>
        </Card>

        {/* Partage des données */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">🚫 Partage des données</h2>
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <span className="text-xl">❌</span>
              <span>Aucune vente de données</span>
            </div>
            <div className="flex items-center gap-2 text-red-400">
              <span className="text-xl">❌</span>
              <span>Aucun partage avec des annonceurs</span>
            </div>
            <div className="flex items-center gap-2 text-red-400">
              <span className="text-xl">❌</span>
              <span>Aucun tracking publicitaire tiers</span>
            </div>
          </div>
          <p className="text-[#B3B6C2] text-sm">
            Les données peuvent être hébergées via des services techniques (hébergement, base de données) 
            strictement nécessaires au fonctionnement de l'application.
          </p>
        </Card>

        {/* Notifications */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-6 h-6 text-[#6E2BFF]" />
            <h2 className="text-xl font-bold">Notifications</h2>
          </div>
          <p className="text-[#9CA0AB] mb-3">ZNO envoie uniquement des notifications liées au jeu :</p>
          <ul className="list-disc list-inside text-[#B3B6C2] space-y-1 mb-4 ml-2">
            <li>duel proposé</li>
            <li>score à confirmer</li>
            <li>perte ou défense d'un terrain</li>
          </ul>
          <div className="bg-[#6E2BFF]/10 border border-[#6E2BFF]/30 rounded-lg p-4">
            <p className="text-white font-semibold">
              👉 Aucune notification marketing.
            </p>
          </div>
        </Card>

        {/* Enfants et mineurs */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">🧒 Enfants et mineurs</h2>
          <p className="text-[#B3B6C2] mb-2">
            ZNO n'est pas spécifiquement destinée aux enfants de moins de 13 ans.
          </p>
          <p className="text-[#B3B6C2]">
            Aucune collecte volontaire de données concernant des mineurs n'est effectuée.
          </p>
        </Card>

        {/* Suppression des données */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-6 h-6 text-[#6E2BFF]" />
            <h2 className="text-xl font-bold">Suppression des données</h2>
          </div>
          <p className="text-[#B3B6C2] mb-4">
            Vous pouvez demander la suppression de votre compte et de vos données à tout moment en écrivant à :
          </p>
          <div className="bg-[#13151D] border border-[#6E2BFF]/30 rounded-lg p-4 mb-3">
            <a 
              href="mailto:team@zno.games" 
              className="flex items-center gap-2 text-[#6E2BFF] hover:text-[#8B5CFF] transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span className="font-semibold">team@zno.games</span>
            </a>
          </div>
          <p className="text-[#9CA0AB] text-sm font-semibold">
            ⚠️ La suppression est définitive.
          </p>
        </Card>

        {/* Contact */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-[#6E2BFF]" />
            <h2 className="text-xl font-bold">Contact</h2>
          </div>
          <p className="text-[#B3B6C2] mb-4">
            Pour toute question concernant la confidentialité :
          </p>
          <a 
            href="mailto:team@zno.games" 
            className="inline-flex items-center gap-2 text-[#6E2BFF] hover:text-[#8B5CFF] transition-colors font-semibold"
          >
            <Mail className="w-5 h-5" />
            team@zno.games
          </a>
        </Card>
      </div>
    </div>
  );
}